import ThemeSwitcher from '../../ui/ThemeSwitcher';
import React, { useState, useEffect } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { t } from '../../utils/i18n';
import { useAuth } from '../../context/AuthContext';
import { login as apiLogin, verify2FA } from '../../api/auth';
import { isDocsRedirectPath } from '../../utils/docsSession';
import LanguageSwitcher from '../../components/LanguageSwitcher/LanguageSwitcher';
import Logo from '../../assets/images/logo.png';
import './Login.css';

function redirectAfterLogin(navigate, target) {
  if (isDocsRedirectPath(target)) {
    window.location.assign(target);
    return;
  }
  navigate(target, { replace: true });
}

export default function Login() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const redirectTo = searchParams.get('redirect') || '/';
  const { login: setAuth, isAuthenticated, loading: authLoading } = useAuth();

  const [step, setStep] = useState(1);
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [code, setCode] = useState('');
  const [tempToken, setTempToken] = useState(null);
  const [transactionId, setTransactionId] = useState(null);
  const [loading, setLoading] = useState(false);
  const [errorModalMessage, setErrorModalMessage] = useState(null);
  const [emailError, setEmailError] = useState(null);
  const [passwordError, setPasswordError] = useState(null);
  const [codeError, setCodeError] = useState(null);
  const [codeExpiresAt, setCodeExpiresAt] = useState(null);
  const [remainingSeconds, setRemainingSeconds] = useState(null);

  const CODE_VALID_SECONDS = 5 * 60; // 5분

  useEffect(() => {
    if (authLoading || !isAuthenticated) return;
    redirectAfterLogin(navigate, redirectTo);
  }, [authLoading, isAuthenticated, navigate, redirectTo]);

  // Step 2: 5분 유효 타이머 (1초마다 갱신)
  useEffect(() => {
    if (step !== 2 || codeExpiresAt == null) {
      setRemainingSeconds(null);
      return;
    }
    const update = () => {
      const sec = Math.max(0, Math.floor((codeExpiresAt - Date.now()) / 1000));
      setRemainingSeconds(sec);
    };
    update();
    const interval = setInterval(update, 1000);
    return () => clearInterval(interval);
  }, [step, codeExpiresAt]);

  if (isAuthenticated) return null;

  // API 응답 data.status(errorcode) → locale 키 매핑 (Login Verify 등)
  const getLoginErrorKey = (err) => {
    const status = err?.data?.data?.status ?? err?.data?.status;
    const retry = err?.data?.data?.retry ?? 0;
    if (status === -1) return 'login.errors.invalidParameter';
    if (status === -401) return retry >= 5 ? 'login.errors.exceedVerificationCode' : 'login.errors.invalidPassword';
    if (status === -404) return 'login.errors.notFound';
    if (status === -410) return 'login.errors.expiredVerificationCode';
    return null;
  };

  const getErrorMessage = (err, fallbackKey) => {
    const raw = err?.data?.message || err?.message || '';
    const networkErrors = [
      'Load failed',
      'Failed to fetch',
      'NetworkError',
      'Network request failed',
    ];
    const isNetworkError =
      networkErrors.some((s) => raw.includes(s)) || err?.name === 'TypeError';
    if (isNetworkError) {
      if (err.authStage === 'email-verification') return t('login.errors.emailVerificationConnection');
      if (err.authStage === 'admin-login' || err.authStage === 'admin-verify') return t('login.errors.adminAuthConnection');
      return t('login.errors.networkError');
    }
    const key = getLoginErrorKey(err);
    if (key) return t(key);
    return raw ? String(raw) : t(fallbackKey);
  };

  const handleStep1 = async (e) => {
    e.preventDefault();
    setErrorModalMessage(null);
    setEmailError(null);
    setPasswordError(null);
    const trimmedEmail = email.trim();
    let hasError = false;
    if (!trimmedEmail) {
      setEmailError(t('login.errors.emailRequired'));
      hasError = true;
    }
    if (!password) {
      setPasswordError(t('login.errors.passwordRequired'));
      hasError = true;
    }
    if (hasError) return;
    setLoading(true);
    try {
      const res = await apiLogin(trimmedEmail, password);
      if (res.requires2fa) {
        setTempToken(res.tempToken);
        setTransactionId(res.transactionId);
        setCodeExpiresAt(Date.now() + CODE_VALID_SECONDS * 1000);
        setStep(2);
      } else {
        setErrorModalMessage(t('login.errors.serverError'));
      }
    } catch (err) {
      if (err.message === 'WAIT_REQUIRED' && err.waitTime != null) {
        setErrorModalMessage(t('login.step1.errorWait', { minutes: err.waitTime }));
      } else if (err.message === 'TOO_MANY_REQUESTS') {
        setErrorModalMessage(t('login.step1.errorTooMany'));
      } else {
        setErrorModalMessage(getErrorMessage(err, 'login.error'));
      }
    } finally {
      setLoading(false);
    }
  };

  const handleStep2 = async (e) => {
    e.preventDefault();
    setErrorModalMessage(null);
    setCodeError(null);
    const normalizedCode = code.replace(/\s/g, '');
    if (normalizedCode.length !== 6 || !/^\d{6}$/.test(normalizedCode)) {
      setCodeError(t('login.step2.errorCodeLength'));
      return;
    }
    setLoading(true);
    try {
      const res = await verify2FA(email, normalizedCode, tempToken, transactionId);
      setAuth(res.user, res.accessToken);
      redirectAfterLogin(navigate, redirectTo);
    } catch (err) {
      const errorKey = getLoginErrorKey(err);
      const msg = errorKey ? t(errorKey) : (err?.data?.message || err?.message || t('login.errors.invalidPassword'));
      setCodeError(msg);
      setErrorModalMessage(getErrorMessage(err, 'login.errors.invalidPassword'));
    } finally {
      setLoading(false);
    }
  };

  const stars = Array.from({ length: 56 }, (_, i) => ({
    id: i,
    left: (i * 17 + 3) % 100,
    top: (i * 23 + 11) % 100,
    delay: (i * 0.12) % 4,
    duration: 2.5 + (i % 3) * 1.2,
  }));

  return (
    <div className="login-page">
      <div className="login-lang-switcher-wrap">
        <ThemeSwitcher /><LanguageSwitcher />
      </div>
      <div className="login-cosmic">
        {stars.map((s) => (
          <span
            key={s.id}
            className="login-star"
            style={{
              left: `${s.left}%`,
              top: `${s.top}%`,
              animationDelay: `${s.delay}s`,
              animationDuration: `${s.duration}s`,
            }}
          />
        ))}
      </div>
      <div className="login-orbit login-nebula-1" />
      <div className="login-orbit login-nebula-2" />
      <div className="login-orbit login-nebula-3" />

      <div className="login-card">
        <div className="login-brand">
          <div className="login-logo-wrap">
            <img src={Logo} alt={t('header.logoAlt')} className="login-logo" />
            <span className="login-logo-text">{t('common.brandName')}</span>
          </div>
          <p className="login-subtitle">
            {step === 1 ? t('login.notice') : t('login.title')}
          </p>
        </div>

        {step === 1 && (
          <form onSubmit={handleStep1} className="login-form">
            <label className="login-label">
              {t('login.step1.email')}
              <input
                type="email"
                value={email}
                onChange={(e) => { setEmail(e.target.value); setEmailError(null); }}
                placeholder={t('login.step1.emailPlaceholder')}
                className={`login-input ${emailError ? 'login-input-error' : ''}`}
                autoComplete="email"
                aria-invalid={!!emailError}
                aria-describedby={emailError ? 'login-email-hint' : undefined}
              />
              {emailError && (
                <span id="login-email-hint" className="login-input-hint" role="alert">
                  {emailError}
                </span>
              )}
            </label>
            <label className="login-label">
              {t('login.step1.password')}
              <input
                type="password"
                value={password}
                onChange={(e) => { setPassword(e.target.value); setPasswordError(null); }}
                className={`login-input ${passwordError ? 'login-input-error' : ''}`}
                autoComplete="current-password"
                aria-invalid={!!passwordError}
                aria-describedby={passwordError ? 'login-password-hint' : undefined}
              />
              {passwordError && (
                <span id="login-password-hint" className="login-input-hint" role="alert">
                  {passwordError}
                </span>
              )}
            </label>
            <button type="submit" className="login-submit" disabled={loading}>
              {loading ? t('common.loading') : t('login.step1.submit')}
            </button>
          </form>
        )}

        {step === 2 && (
          <form onSubmit={handleStep2} className="login-form">
            <p className="login-step2-desc">
              {t('login.step2.description', { email })}
            </p>
            <div className="login-step2-timer" aria-live="polite">
              {remainingSeconds != null && remainingSeconds > 0 ? (
                <span className="login-timer-remaining">
                  {t('login.step2.timerRemaining', {
                    time: `${Math.floor(remainingSeconds / 60)}:${String(remainingSeconds % 60).padStart(2, '0')}`,
                  })}
                </span>
              ) : remainingSeconds === 0 ? (
                <span className="login-timer-expired">
                  {t('login.step2.timerExpired')}
                </span>
              ) : null}
            </div>
            <label className="login-label">
              {t('login.step2.title')}
              <div className="login-otp-wrap">
                <input
                  type="text"
                  inputMode="numeric"
                  maxLength={6}
                  value={code}
                  onChange={(e) => { setCode(e.target.value.replace(/\D/g, '')); setCodeError(null); }}
                  placeholder={t('login.step2.codePlaceholder')}
                  className={`login-input login-otp ${codeError ? 'login-input-error' : ''}`}
                  autoFocus
                  aria-invalid={!!codeError}
                  aria-describedby={codeError ? 'login-code-hint' : undefined}
                />
              </div>
              {codeError && (
                <span id="login-code-hint" className="login-input-hint" role="alert">
                  {codeError}
                </span>
              )}
            </label>
            <div className="login-step2-actions">
              <button
                type="button"
                className="login-back"
                onClick={() => { setStep(1); setCode(''); setErrorModalMessage(null); setCodeError(null); setCodeExpiresAt(null); setTempToken(null); setTransactionId(null); }}
                disabled={loading}
              >
                {t('login.step2.back')}
              </button>
              <button type="submit" className="login-submit" disabled={loading || remainingSeconds === 0}>
                {loading ? t('common.loading') : t('login.step2.submit')}
              </button>
            </div>
          </form>
        )}
      </div>

      {loading && (
        <div className="login-loading-overlay" aria-busy="true" aria-live="polite">
          <div className="login-spinner" aria-hidden="true" />
          <p className="login-loading-text">{t('common.loading')}</p>
        </div>
      )}

      {errorModalMessage && (
        <div
          className="login-modal-overlay"
          role="dialog"
          aria-modal="true"
          aria-labelledby="login-error-title"
          onClick={() => setErrorModalMessage(null)}
        >
          <div
            className="login-modal-card"
            onClick={(e) => e.stopPropagation()}
          >
            <h2 id="login-error-title" className="login-modal-title">
              ⚠️ 오류
            </h2>
            <p className="login-modal-message">{errorModalMessage}</p>
            <button
              type="button"
              className="login-modal-confirm"
              onClick={() => setErrorModalMessage(null)}
            >
              확인
            </button>
          </div>
        </div>
      )}
    </div>
  );
}
